// Top bar from the UX concepts: logo, Plan · Library · Maps, search, the support assistant, Subscribe and account.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useSearchParams } from 'react-router';
import { ProfileDialog } from '@/auth/ProfileDialog';
import { setCrosshairs, useMapState } from '@/map/mapState';
import { openSubscribe } from '@/features/billing/billing';
import { premiumFeatures } from '@/auth/me';
import type { PremiumFeature } from '@/auth/me';
import { SubscribeDialog } from '@/features/billing/SubscribeDialog';
import { libraryHome } from '@/features/library/session';
import { clearDraftStore } from '@/features/plan/draftStore';
import { useDraftStore, useDraftStoreUser } from '@/features/plan/useDraftStore';
import { SearchBox } from '@/features/search/SearchBox';
import { HeadsetIcon, SupportPanel } from '@/features/support/SupportPanel';
import { Avatar } from '@/people/Avatar';
import { useAccount } from '@/auth/useAccount';
import styles from './AppShell.module.css';

export function AppShell() {
  const { t } = useTranslation();
  // Re-render on every navigation so the Library tab picks up the folder the Library last had open.
  useLocation();
  const [supportOpen, setSupportOpen] = useState(false);
  // Unsaved planning work is kept in the browser from here, whichever page is open.
  useDraftStore();
  const closeSupport = useCallback(() => setSupportOpen(false), []);
  // ?subscribe=<feature> (XpeditionGO's "Sign up now" opens this page): Subscribe, for that feature.
  const [search, setSearch] = useSearchParams();
  const subscribeFor = search.get('subscribe');
  useEffect(() => {
    if (subscribeFor === null) return;
    const feature = premiumFeatures.find((f) => f === subscribeFor) as PremiumFeature | undefined;
    openSubscribe(feature ? { feature } : true);
    setSearch(
      (params) => {
        params.delete('subscribe');
        return params;
      },
      { replace: true },
    );
  }, [subscribeFor, setSearch]);
  const tabs = [
    { to: '/plan', label: t('nav.plan') },
    // Back to the folder the user was in.
    { to: libraryHome(), label: t('nav.library') },
    { to: '/maps', label: t('nav.maps') },
    { to: '/activities', label: t('nav.activities') },
  ];

  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <span className={styles.logo}>
          Xpedition<span className={styles.logoAccent}>2</span>
        </span>
        <nav className={styles.tabs} aria-label="Main">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) => (isActive ? `${styles.tab} ${styles.active}` : styles.tab)}
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
        <SearchBox />
        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.support} ${supportOpen ? styles.supportOpen : ''}`}
            aria-label={t('support.button')}
            title={t('support.button')}
            aria-expanded={supportOpen}
            onClick={() => setSupportOpen((value) => !value)}
          >
            <HeadsetIcon />
          </button>
          <button type="button" className={styles.subscribe} onClick={() => openSubscribe()}>
            {t('nav.subscribe')}
          </button>
          <AccountMenu />
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
      <SupportPanel open={supportOpen} onClose={closeSupport} />
      <SubscribeDialog />
    </div>
  );
}

/** The avatar: opens a menu with the user's name, their profile, map options and sign out. */
function AccountMenu() {
  const { t } = useTranslation();
  const account = useAccount();
  const draftUser = useDraftStoreUser();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { crosshairs } = useMapState();
  const container = useRef<HTMLDivElement>(null);

  // Close on a click elsewhere or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.account} ref={container}>
      <button
        type="button"
        className={styles.avatar}
        title={account.name}
        aria-label={t('nav.account')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar personGuid={account.personGuid} initials={account.initials} />
      </button>
      {open && (
        <div className={styles.menu} role="menu">
          <div className={styles.menuHeader}>
            <div className={styles.menuName}>{account.name}</div>
            {account.email && <div className={styles.menuEmail}>{account.email}</div>}
          </div>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              setProfileOpen(true);
            }}
          >
            {t('nav.profile')}
          </button>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={crosshairs}
            className={`${styles.menuItem} ${styles.menuCheck}`}
            onClick={() => setCrosshairs(!crosshairs)}
          >
            <span className={styles.menuTick} aria-hidden="true">
              {crosshairs ? '✓' : ''}
            </span>
            {t('nav.crosshairs')}
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            // Unsaved work kept in this browser goes with the sign-in (it may be a shared computer).
            onClick={() =>
              void (draftUser ? clearDraftStore(draftUser) : Promise.resolve()).finally(account.signOut)
            }
          >
            {t('nav.signOut')}
          </button>
        </div>
      )}
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
